// File: frontend/src/components/pages/home/TrustSection.js

import React from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../hooks/useLanguage';
import { Lock, Eye, Trash2, ArrowRight, Shield } from 'lucide-react';
import ScrollAnimation from '../../common/ScrollAnimation';
import AuthModal from '../../auth/AuthModal';
import styles from './TrustSection.module.css';

const TrustSection = () => {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = React.useState(false);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard/policies');
    } else {
      setShowAuthModal(true);
    }
  };

  const TRUST_POINTS = [
    {
      id: 'no-sell',
      icon: Eye,
      title: t('trust.point1.title', 'We never sell your data'),
      description: t('trust.point1.description', 'Your documents are yours. We don\'t share them with insurers, advertisers, or anyone else. Ever.'),
    },
    {
      id: 'encryption',
      icon: Lock,
      title: t('trust.point2.title', 'Encrypted like your bank'),
      description: t('trust.point2.description', 'Every file you upload is protected with the same security used by major banks — in storage and in transit.'),
    },
    {
      id: 'delete',
      icon: Trash2,
      title: t('trust.point3.title', 'Delete anytime, gone forever'),
      description: t('trust.point3.description', 'Cancel your account and we wipe everything. No backups, no archives, no exceptions.'),
    },
  ];

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <ScrollAnimation>
          <div className={styles.header}>
            <span className={styles.label}>
              <Shield size={14} strokeWidth={2.5} />
              {t('trust.label', 'Your Privacy')}
            </span>
            <h2 className={styles.title}>{t('trust.title', 'Your Data. Your Control.')}</h2>
            <p className={styles.subtitle}>
              {t('trust.subtitle', 'Insurance documents are personal. Here\'s how we keep them safe.')}
            </p>
          </div>
        </ScrollAnimation>

        <div className={styles.grid}>
          {TRUST_POINTS.map((point, i) => {
            const IconComponent = point.icon;
            return (
              <ScrollAnimation key={point.id} delay={i * 100}>
                <div className={styles.card}>
                  <div className={styles.cardIcon}>
                    <IconComponent size={24} strokeWidth={2} />
                  </div>
                  <h3 className={styles.cardTitle}>{point.title}</h3>
                  <p className={styles.cardDescription}>{point.description}</p>
                </div>
              </ScrollAnimation>
            );
          })}
        </div>

        {/* CTA */}
        <ScrollAnimation delay={200}>
          <div className={styles.ctaWrapper}>
            <button className={styles.cta} onClick={handleGetStarted}>
              <span>
                {isAuthenticated
                  ? t('trust.cta_primary_auth', 'Go to Wallet')
                  : t('trust.cta', 'Get Started Free')
                }
              </span>
              <ArrowRight size={16} strokeWidth={2.5} />
            </button>
          </div>
        </ScrollAnimation>

        {/* Stats bar */}
        <ScrollAnimation delay={300}>
          <div className={styles.statsBar}>
            <div className={styles.stat}>
              <span className={styles.statValue}>{t('trust.stat1.value', '10,000+')}</span>
              <span className={styles.statLabel}>{t('trust.stat1.label', 'Policyholders')}</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.stat}>
              <span className={styles.statValue}>{t('trust.stat2.value', '50+')}</span>
              <span className={styles.statLabel}>{t('trust.stat2.label', 'Insurers Supported')}</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.stat}>
              <span className={styles.statValue}>{t('trust.stat3.value', '$2.4B+')}</span>
              <span className={styles.statLabel}>{t('trust.stat3.label', 'Coverage Analyzed')}</span>
            </div>
          </div>
        </ScrollAnimation>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialType="sign_up"
      />
    </section>
  );
};

export default TrustSection;